/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { AddLangToPath } from "@/helper/AddLanguagePath";

// Site Detection

/**
 * Returns true when the hostname is an RFC-1918 private IPv4 address.
 *
 * Covers all three private ranges (RFC 1918):
 *   10.0.0.0/8       — starts with "10."
 *   172.16.0.0/12    — starts with "172." and second octet 16–31
 *   192.168.0.0/16   — starts with "192.168."
 *
 * Docker's default bridge network sits at 172.17.x.x (inside the 16–31 band),
 * so this correctly identifies container addresses as local.
 * A public 172.x address outside the 16–31 band is correctly excluded.
 */
function isPrivateIpv4(hostname: string): boolean {
  if (hostname.startsWith("10.")) return true;
  if (hostname.startsWith("192.168.")) return true;
  if (hostname.startsWith("172.")) {
    const parts = hostname.split(".");
    const second = parseInt(parts[1] ?? "", 10);
    return second >= 16 && second <= 31;
  }
  return false;
}

/**
 * Check if the current site is the main Deskshare marketing site.
 *
 * Returns true for:
 *   - deskshare.com and www.deskshare.com (production)
 *   - Any *.deskshare.com subdomain EXCEPT api.deskshare.com, *.api.deskshare.com,
 *     shop.deskshare.com, and *.shop.deskshare.com (staging hosts)
 *   - localhost (developer machines)
 *   - All three RFC 1918 private IPv4 ranges (developer machines and Docker containers)
 *
 * This ensures staging and development hosts route sign-in to /auth (merged
 * login) rather than falling through to shop behaviour.
 * The api and shop subdomains are explicitly excluded so they keep their own routing.
 *
 * Item 22 fix: previously only matched the two production hostnames, so a staging
 * host was treated as "not the marketing site" and its sign-in entry resolved to
 * the shop instead of /auth.
 *
 * SC-3: Dev/staging override — set VITE_FORCE_DESKSHARE_CART=true in .env.local
 * to test the Deskshare store path without deploying to deskshare.com.
 * Production builds leave this unset (defaults to false).
 */
export function isDeskshare(): boolean {
  const forceCart = import.meta.env.VITE_FORCE_DESKSHARE_CART;
  if (forceCart === "true") return true;
  if (forceCart === "false") return false;

  try {
    // Strip port so "localhost:5173" and "172.17.0.2:8080" match correctly.
    const hostname = new URL(window.location.href).hostname;

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") return true;

    // RFC 1918 private ranges (developer machines and Docker containers)
    if (isPrivateIpv4(hostname)) return true;

    // Production hosts
    if (hostname === "deskshare.com" || hostname === "www.deskshare.com")
      return true;

    // Staging subdomains: any *.deskshare.com EXCEPT api and shop subdomains.
    // The explicit deny-list prevents api.deskshare.com and shop.deskshare.com
    // from matching the broad subdomain pattern.
    if (hostname.endsWith(".deskshare.com")) {
      if (
        hostname === "api.deskshare.com" ||
        hostname.endsWith(".api.deskshare.com") ||
        hostname === "shop.deskshare.com" ||
        hostname.endsWith(".shop.deskshare.com")
      ) {
        return false;
      }
      return true;
    }

    return false;
  } catch (error) {
    // URL construction from window.location.href should never fail in a browser,
    // so report this as an unexpected error if it occurs.
    (
      window as Window & { Sentry?: { captureException: (e: unknown) => void } }
    ).Sentry?.captureException(error);
    return false;
  }
}

// URL Helpers

/**
 * Get the JWT-consumer shopping cart URL.
 *
 * Decision 2 (approved): this variable names the SINGLE origin that validates
 * Deskshare JWTs. The fallback is https://shop.deskshare.com (the new shop),
 * not https://store.deskshare.com (the legacy ASP.NET account site).
 *
 * Why: the JWT audience (aud claim) and this URL must be identical strings.
 * store.deskshare.com does not validate Deskshare tokens; using it as the
 * audience causes every token request to return 400, dropping auth silently
 * on all three sign-in paths (password login, reset, Google OAuth).
 *
 * Set VITE_REACT_APP_SHOPPING_CART_URL=https://shop.deskshare.com in .env
 * and match it exactly with ALLOWED_JWT_AUDIENCES in the backend .env.
 *
 * @returns The base shopping cart / JWT-consumer URL
 */
export function getStoreBaseUrl(): string {
  return (
    import.meta.env.VITE_REACT_APP_SHOPPING_CART_URL ||
    "https://shop.deskshare.com"
  );
}

/**
 * Get the legacy ASP.NET store URL for account/dashboard links on isDeskshare()
 * marketing hosts. This is a separate variable because the legacy store uses
 * session cookies (not JWT tokens), so it is a different destination from the
 * JWT-consuming shop.
 *
 * Decision 2 note: if the new shop dashboard is the intended account landing
 * page, remove this variable and use getStoreBaseUrl() throughout. This is a
 * product call — see HANDOFF.md for the open question.
 *
 * @returns The legacy store base URL
 */
export function getLegacyStoreUrl(): string {
  return (
    import.meta.env.VITE_REACT_APP_LEGACY_STORE_URL ||
    "https://store.deskshare.com"
  );
}

/**
 * Generate the user account/dashboard URL based on current site context.
 *
 * isDeskshare() hosts use the legacy ASP.NET account page (session-based,
 * no JWT involved). Other hosts (shop) use the new dashboard route.
 *
 * @param language - The current language code (e.g., 'en', 'de')
 * @returns The appropriate account URL for the current site
 */
export function getAccountUrl(language: string = "en"): string {
  if (isDeskshare()) {
    // Legacy store account page — uses its own session, not a Deskshare JWT.
    return `${getLegacyStoreUrl()}/Members/MyAccount.aspx?lang=eng`;
  }

  const storeUrl = getStoreBaseUrl();
  return `${storeUrl}/${language}/dashboard/overview`;
}

/**
 * Merged auth login URL on the marketing site (/auth with optional lang prefix).
 * Shop hosts keep using getDashboardUrl() for sign-in entry.
 */
export function getAuthLoginUrl(): string {
  return AddLangToPath("/auth");
}

/**
 * Generate the add-to-cart URL for a product.
 *
 * @param productId - The product ID to add to cart
 * @param language - The current language code
 * @returns The add-to-cart URL
 */
export function getAddToCartUrl(
  productId: string | number,
  language: string = "en",
): string {
  const storeUrl = getStoreBaseUrl();

  if (isDeskshare()) {
    const langCode = LANG_MAPPER[language] || "eng";
    // Legacy store uses the ASP.NET AddProduct URL regardless of JWT flow.
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${productId}&Lang=${langCode}`;
  }

  return `${storeUrl}/?add-to-cart=${productId}`;
}

/**
 * Generate the cart URL for the current site.
 *
 * @param language - The current language code
 * @returns The cart URL
 */
export function getCartUrl(language: string = "en"): string {
  if (isDeskshare()) {
    return `${getLegacyStoreUrl()}/Basket.aspx`;
  }

  const storeUrl = getStoreBaseUrl();
  return `${storeUrl}/${language}/cart/`;
}

/**
 * Generate the dashboard/sign-in URL for the current site.
 *
 * @param language - The current language code
 * @returns The dashboard URL
 */
export function getDashboardUrl(language: string = "en"): string {
  if (isDeskshare()) {
    return `${getLegacyStoreUrl()}/Members/MyAccount.aspx?lang=eng`;
  }

  const storeUrl = getStoreBaseUrl();
  return `${storeUrl}/${language}/dashboard/overview`;
}

/**
 * Generate a bulk add-to-cart URL for multiple items of the same product.
 *
 * @param categoryId - The category/product ID
 * @param count - Number of items
 * @returns The bulk add-to-cart URL
 */
export function getAddToCartBulkUrl(categoryId: number, count: number): string {
  if (isDeskshare()) {
    // Legacy store does not support bulk add-to-cart via a single URL parameter.
    // Use the standard single-product URL with quantity — the store accumulates.
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${categoryId}&Qty=${count}`;
  }

  const storeUrl = getStoreBaseUrl();
  const ids = Array(count).fill(categoryId).join(",");
  return `${storeUrl}/?add-to-cart=${ids}`;
}

// ---------------------------------------------------------------------------
// V3: Buy Now link resolver — routes to the correct store per product type
// ---------------------------------------------------------------------------

/**
 * Input shape for the Buy Now link resolver.
 * All fields are optional so callers don't need to know which branch applies;
 * the resolver picks the correct path from whatever data is available.
 */
export interface PlanLinkInput {
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  legacyCategoryId?: number | null;
  slug?: string | null;
  language?: string;
}

/**
 * Build the real add-to-cart URL for a Stripe product via the new shop.
 *
 * The shop reads productId and priceId from the URL query string. The path
 * segment is kept in this one helper — a single-string change here propagates
 * to every Buy Now link, with no changes needed at call sites (Decision 3).
 */
export function getStripeAddToCartUrl(
  stripeProductId: string,
  stripePriceId: string,
  language?: string,
): string {
  const storeUrl = getStoreBaseUrl();
  const lang = encodeURIComponent(language ?? "en");
  return `${storeUrl}/${lang}/add-to-cart?productId=${encodeURIComponent(stripeProductId)}&priceId=${encodeURIComponent(stripePriceId)}`;
}

/**
 * Determine the real navigation target for a Buy Now click.
 *
 * Priority order (load-bearing — do not reorder):
 *  1. Both Stripe IDs present → new shop add-to-cart. This check runs FIRST
 *     so a migrated product never falls back to the legacy store, even on
 *     a deskshare.com marketing host.
 *  2. Marketing/localhost host with a legacy category ID → legacy ASP.NET store.
 *  3. Non-marketing host with a legacy category ID → new shop (transitional).
 *  4. No usable ID → "#" (disabled state; isCartReady guard prevents activation).
 */
export function getPlanBuyNowUrl(input: PlanLinkInput): string {
  // 1. Migrated product: both Stripe IDs present → new shop.
  if (input.stripeProductId && input.stripePriceId) {
    return getStripeAddToCartUrl(
      input.stripeProductId,
      input.stripePriceId,
      input.language,
    );
  }

  // 2. Marketing / legacy-cart host with a category ID → old ASP.NET store.
  if (isDeskshare() && input.legacyCategoryId != null) {
    const lc = LANG_MAPPER[input.language ?? "en"] || "eng";
    return `${getLegacyStoreUrl()}/AddProduct.aspx?ProductId1=${input.legacyCategoryId}&Lang=${lc}`;
  }

  // 3. Transitional: shop host, product not yet migrated → legacy add-to-cart param.
  if (input.legacyCategoryId != null) {
    return `${getStoreBaseUrl()}/?add-to-cart=${input.legacyCategoryId}`;
  }

  return "#";
}

/**
 * Build a readable hover URL for the status bar — shows a clean slug-based
 * URL instead of exposing Stripe IDs.
 *
 * Falls back to the real URL when there is no cleaner alternative, so hover
 * always shows a URL that matches where activation actually goes.
 *
 * This is the href-swap interim model (Decision 2, Option B). When the shop
 * adds a server-side slug route (?product=dictation-annual → Stripe IDs), the
 * friendly URL becomes the real URL and the swap is no longer needed.
 */
export function getPlanHoverUrl(input: PlanLinkInput): string {
  if (input.stripeProductId && input.stripePriceId && input.slug) {
    const lang = encodeURIComponent(input.language ?? "en");
    return `${getStoreBaseUrl()}/${lang}/add-to-cart?product=${encodeURIComponent(input.slug)}`;
  }
  return getPlanBuyNowUrl(input);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Language code mapping for legacy store URLs.
 * Maps ISO language codes to store-specific language parameters.
 */
export const LANG_MAPPER: Record<string, string> = {
  en: "eng",
  de: "ge",
  fr: "fr",
  es: "sp",
  pt: "po",
  it: "it",
};
